// S4 of ClickUp 86d42x0pt — the two consult chat attachment uploads.
//
// A file cannot travel over Socket.IO (talk's buffer is capped and already carries the consult
// audio), so it goes to healthcare and only its metadata rides the socket.

import { ENV } from "../../../config/runtimeEnv";
import { ConsultChatAttachment } from "../../../types/appointments/ConsultChatTypes";
import { http } from "../../../lib/http/client";

/** ⚠️ ALREADY ENDS IN `/api` in every ConfigMap — hence `${BASE}/patient/...`, never `/api/patient/...`. */
const HEALTHCARE_SERVER = ENV.VITE_HEALTHCARE_SERVER;

/** Fixed sentences, never the server's body: a 404 here must read the same whatever caused it. */
const UPLOAD_ERROR_MESSAGES: Record<number, string> = {
	404: "This consultation is no longer available, so the file was not sent.",
	413: "That file is too large. Attach a PDF, Word document or image under 20 MB.",
	422: "That file was not accepted. Attach a PDF, Word document or image under 20 MB.",
	502: "The file could not be stored. Please try again.",
};

const GENERIC_UPLOAD_ERROR = "The file could not be uploaded. Please try again.";

export class ConsultChatUploadError extends Error {
	readonly status: number | null;

	constructor(message: string, status: number | null) {
		super(message);
		this.name = "ConsultChatUploadError";
		this.status = status;
	}
}

const statusOf = (error: unknown): number | null => {
	const status = (error as { response?: { status?: unknown } })?.response?.status;
	return typeof status === "number" ? status : null;
};

// ⛔ NOTHING on this path is logged. An axios error carries `config.data`, which on the patient
// call IS the consultation link — the patient's whole credential.
const asUploadError = (error: unknown): ConsultChatUploadError => {
	const status = statusOf(error);
	const message = (status !== null && UPLOAD_ERROR_MESSAGES[status]) || GENERIC_UPLOAD_ERROR;
	return new ConsultChatUploadError(message, status);
};

const toAttachments = (payload: unknown): ConsultChatAttachment[] => {
	const files = (payload as { files?: unknown })?.files;
	if (!Array.isArray(files)) return [];

	return files
		.filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
		.map((row) => ({
			id: String(row.id ?? ""),
			name: String(row.name ?? ""),
			mime: String(row.mime ?? ""),
			size: typeof row.size === "number" ? row.size : Number(row.size) || 0,
		}));
};

/**
 * The patient half. **No session needed** — the patient may have no account at all and the
 * consultation link is the whole credential (healthcare answers every refusal with a 404).
 */
export const uploadPatientChatAttachment = async (consultationLink: string, file: File): Promise<ConsultChatAttachment[]> => {
	const form = new FormData();
	form.append("consultation_link", consultationLink);
	form.append("file", file);

	try {
		const response = await http.post(`${HEALTHCARE_SERVER}/patient/consultations/chat-attachment`, form);
		return toAttachments(response.data);
	} catch (error) {
		throw asUploadError(error);
	}
};

/** The practitioner half — an `access-practitioner-system` bearer is the whole boundary. */
export const uploadPractitionerChatAttachment = async (appId: number, file: File): Promise<ConsultChatAttachment[]> => {
	const form = new FormData();
	form.append("app_id", String(appId));
	form.append("file", file);

	try {
		const response = await http.post(`${HEALTHCARE_SERVER}/practitioner/consultations/chat-attachment`, form);
		return toAttachments(response.data);
	} catch (error) {
		throw asUploadError(error);
	}
};
